/**
 * Lisa AI Image Preview Feature
 * Custom element that extends ModalDialog to handle engraved preview generation.
 */

if (!customElements.get('lisa-ai-image-modal')) {
  customElements.define(
    'lisa-ai-image-modal',
    class LisaAiImageModal extends ModalDialog {
      // ─── Static Constants ─────────────────────────────────────────────────────

      static generationTimeoutMs = 180000; // Max wait time for image generation (ms)
      static statusPollIntervalMs = 3000; // Delay between status check polls (ms)
      static loaderTextRotationMs = 5000; // Interval for cycling loader text messages (ms)
      static progressDurationMs = 38000; // Progress reaches max in 30s
      static progressMaxPercent = 99; // Cap at 99% until generation finishes

      static selectors = {
        nativeCloseBtn: '.product-popup-modal__toggle',
        closeButton: '.lisa-ai-modal__close-button',
        previewButton: '.lisa-ai-modal__button',
        previewButtonLabel: '.lisa-ai-modal__button-label',
        popupContent: '.lisa-ai-modal__popup-content',
        loadingContainer: '.lisa-ai-modal__loading, .lisa-ai-modal__preview',
        loaderContainer: '.lisa-ai-modal__loader',
        loaderText: '.lisa-ai-modal__loader-text',
        progressContainer: '.lisa-ai-modal__progress',
        progressFill: '.lisa-ai-modal__progress-fill',
        progressPercent: '.lisa-ai-modal__progress-percent',
        resultContainer: '.lisa-ai-modal__result',
        resultMediaWrap: '.lisa-ai-modal__result-media-wrap',
        errorMessage: '.lisa-ai-modal__error',
        avpOption: '.avp-option',
        magnifyIcon: '.product__media-icon--lightbox',
        spinner: '.loading__spinner',
      };

      static classes = {
        previewButton: 'lisa-ai-modal__button',
        loaderText: 'lisa-ai-modal__loader-text',
        isVisible: 'is-visible',
        resultImage: 'lisa-ai-modal__result-image',
        isLoaded: 'is-loaded',
      };

      static states = {
        idle: 'idle',
        generating: 'generating',
        success: 'success',
        error: 'error',
      };

      static analyticsEvents = {
        previewButtonSeen: 'lisa_ai:preview_button_seen',
        previewGenerateStarted: 'lisa_ai:preview_generate_started',
        previewGenerated: 'lisa_ai:preview_generate_finished',
        previewGenerateError: 'lisa_ai:preview_generate_error',
        previewPopupSeen: 'lisa_ai:preview_popup_seen',
        addToCart: 'lisa_ai:add_to_cart',
      };

      // ─── Constructor & Lifecycle ──────────────────────────────────────────────

      constructor() {
        super();

        this._settings = JSON.parse(this.querySelector('[data-json-settings]')?.textContent || '{}');
        this._loaderInterval = null;
        this._progressInterval = null;
        this._progressStartTime = null;
        this._generationInProgress = false;
        this._firstTextFieldProcessed = false;
        this._lightbox = null;
        this._lastEngravingText = null;
        this._lastSuccessfulEngravingText = null;
        this._lastSuccessfulImageUrl = null;
        this._lastSuccessfulShopifyFilename = null;
        this._pendingPreviewSeen = null;
        this._generationStartTime = null;
        this._generationAbortController = null;
        this._generationRequestId = 0;
        this._isPreviewButtonLoading = false;
        this._togglePreviewButtonState = null;

        this.elements = {
          previewButton: null,
          previewInput: null,
          productForm: null,
          popupContent: this.querySelector(LisaAiImageModal.selectors.popupContent),
          loadingContainer: this.querySelector(LisaAiImageModal.selectors.loadingContainer),
          loaderContainer: this.querySelector(LisaAiImageModal.selectors.loaderContainer),
          progressContainer: this.querySelector(LisaAiImageModal.selectors.progressContainer),
          progressFill: this.querySelector(LisaAiImageModal.selectors.progressFill),
          progressPercent: this.querySelector(LisaAiImageModal.selectors.progressPercent),
          resultContainer: this.querySelector(LisaAiImageModal.selectors.resultContainer),
          resultMediaWrap: this.querySelector(LisaAiImageModal.selectors.resultMediaWrap),
          errorMessage: this.querySelector(LisaAiImageModal.selectors.errorMessage),
          closeButton: this.querySelector(LisaAiImageModal.selectors.closeButton),
          magnifyIcon: this.querySelector(LisaAiImageModal.selectors.magnifyIcon),
          nativeCloseBtn: this.querySelector(LisaAiImageModal.selectors.nativeCloseBtn),
          spinner: this.querySelector(LisaAiImageModal.selectors.spinner),
        };
      }

      connectedCallback() {
        this.style.setProperty('--lisa-ai-loader-rotation-duration', `${LisaAiImageModal.loaderTextRotationMs}ms`);
        this._setModalState(LisaAiImageModal.states.idle);
        this._setupCloseButtons();
        this._setupAvisOptionsButton();

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartInterceptor.add, (event) => this._onCartAdded(event));
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      show(opener) {
        super.show(opener);
        this._firePendingPreviewSeen();
      }

      // ─── Setup ────────────────────────────────────────────────────────────────

      _setupCloseButtons() {
        const { nativeCloseBtn, closeButton } = this.elements;

        // Native icon-close button in modal header — just dismisses the modal,
        // leaves any in-progress generation running so the user can re-open it.
        if (nativeCloseBtn) {
          nativeCloseBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.hide();
          });
        }

        // Our styled close button shown after generation completes
        if (closeButton) {
          closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            this._teardownOnClose();
            this.hide();
          });
        }
      }

      _onCartAdded(event) {
        if (!this._lastSuccessfulImageUrl) return;

        let items;
        try {
          items = JSON.parse(event.body).items;
        } catch {
          return;
        }

        // Check if any of the added items has our generated image URL in its properties,
        // safe guard if we have multiple product forms on the page and the user generated previews for more than one product before adding to cart.
        const aiItem = items.find(item => item.properties?.['_ai_preview_image_url'] === this._lastSuccessfulImageUrl);
        if (!aiItem) return;

        this._publishAnalytics(LisaAiImageModal.analyticsEvents.addToCart, {
          engravingText: this._lastSuccessfulEngravingText,
          imageUrl: this._lastSuccessfulImageUrl,
          shopifyFilename: this._lastSuccessfulShopifyFilename,
        });
      }

      _setupAvisOptionsButton() {
        document.addEventListener('productoptions.fieldLoad', (event) => {
          if (this._firstTextFieldProcessed) return;
          if (event.detail.fieldConfig.type !== 'text') return;

          this._firstTextFieldProcessed = true;
          this._injectButton(event.detail.element.get(0));
        });
      }

      _injectButton(fieldElement) {
        const settings = this._settings;
        if (!fieldElement || !settings.buttonText) return;

        const avpOption = fieldElement.closest(LisaAiImageModal.selectors.avpOption);
        if (!avpOption) return;

        this.elements.productForm = avpOption.closest('product-form form[data-type="add-to-cart-form"]');

        const inputElement = fieldElement.tagName === 'INPUT'
          ? fieldElement
          : fieldElement.querySelector('input');

        if (!inputElement) return;
        this.elements.previewInput = inputElement;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = LisaAiImageModal.classes.previewButton + ' button is-empty';
        button.dataset.defaultText = settings.buttonText;
        const buttonSpan = document.createElement('span');
        buttonSpan.className = 'lisa-ai-modal__button-label';
        buttonSpan.textContent = settings.buttonText;
        button.appendChild(buttonSpan);

        const buttonWrap = document.createElement('div');
        buttonWrap.className = 'lisa-ai-modal__button-wrap u-flex u-flex-col';
        buttonWrap.appendChild(button);

        let buttonEmptyText = null;
        if (settings.buttonEmptyCustomizeFieldText) {
          buttonEmptyText = document.createElement('span');
          buttonEmptyText.className = 'lisa-ai-modal__button-empty-text left u-w-full hidden';
          buttonEmptyText.textContent = settings.buttonEmptyCustomizeFieldText;
          buttonWrap.appendChild(buttonEmptyText);
        }

        avpOption.appendChild(buttonWrap);
        this.elements.previewButton = button;
        this.elements.buttonEmptyText = buttonEmptyText;
        this._enforceGenerationLimit();

        const startButtonSeenObserver = () => {
          const io = new IntersectionObserver((entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.disconnect();
            this._publishAnalytics(LisaAiImageModal.analyticsEvents.previewButtonSeen);
          }, { threshold: 0.5 });
          io.observe(button);
        };

        // Avis sets disabled="disabled" on the avp-option while it is hidden/initializing
        // and removes it once the field is fully ready and visible. Wait for that removal
        // before starting IntersectionObserver so it only fires on genuine user scroll.
        if (!avpOption.hasAttribute('disabled')) {
          startButtonSeenObserver();
        } else {
          const visibilityWatcher = new MutationObserver(() => {
            if (avpOption.hasAttribute('disabled')) return;
            visibilityWatcher.disconnect();
            startButtonSeenObserver();
          });
          visibilityWatcher.observe(avpOption, { attributes: true, attributeFilter: ['disabled'] });
        }

        const toggleButtonState = () => {
          const currentValue = inputElement.value?.trim() || '';
          button.classList.toggle('is-empty', !currentValue.length);
          this._syncPreviewButtonLoadingState(currentValue);
          if (buttonEmptyText && currentValue.length) {
            buttonEmptyText.classList.add('hidden');
          }
        };

        this._togglePreviewButtonState = toggleButtonState;

        inputElement.addEventListener('input', toggleButtonState);
        inputElement.addEventListener('change', toggleButtonState);
        toggleButtonState();

        button.addEventListener('click', (e) => {
          e.preventDefault();
          const engravingText = inputElement.value?.trim();
          if (!engravingText) {
            if (buttonEmptyText) buttonEmptyText.classList.remove('hidden');
            return;
          }
          this._generateAndShow(engravingText);
        });
      }

      // ─── Orchestration ────────────────────────────────────────────────────────

      async _generateAndShow(engravingText) {
        const latestRequestId = this._generationRequestId + 1;

        if (this._generationInProgress && engravingText !== this._lastEngravingText) {
          // Text changed mid-generation — cancel and restart with new text.
          this._abortCurrentGeneration();
        }

        if (this._generationInProgress) {
          this._showModal();
          return;
        }

        if (this._lastSuccessfulEngravingText === engravingText && Boolean(this._getResultImageElement())) {
          this._applySuccessUI();
          this._showModal();
          return;
        }

        this._generationRequestId = latestRequestId;
        this._generationInProgress = true;
        this._lastEngravingText = engravingText;
        this._generationAbortController = new AbortController();
        this._publishAnalytics(LisaAiImageModal.analyticsEvents.previewGenerateStarted, { engravingText });
        this._generationStartTime = Date.now();

        this._applyGeneratingUI(engravingText);
        this._showModal();

        try {
          const { imageUrl: resultImageUrl, shopifyFilename, wasCached } = await this._fetchPreview(engravingText, this._generationAbortController.signal);

          if (latestRequestId !== this._generationRequestId) return;

          if (this.elements.resultContainer) {
            const img = await this._createResultImage(resultImageUrl);
            this._resetResultContainer({ lightboxMediaUrl: resultImageUrl });
            const resultImageHost = this.elements.resultMediaWrap || this.elements.resultContainer;
            resultImageHost.appendChild(img);
            this.elements.resultContainer.classList.add(LisaAiImageModal.classes.isVisible);

            requestAnimationFrame(() => img.classList.add(LisaAiImageModal.classes.isLoaded));

            if (typeof SimpleLightbox !== 'undefined') {
              if (this._lightbox) this._lightbox.destroy();
              const closeText = document.querySelector(`#${this.id}-Close`)?.innerHTML;
              this._lightbox = new SimpleLightbox(`#${this.id} ${LisaAiImageModal.selectors.resultMediaWrap}`, {
                closeText: closeText || '&times;',
                overlayOpacity: 0.9,
                sourceAttr: 'data-lightbox-media',
                className: 'lisa-ai-simple-lightbox',
                captions: false,
                animationSpeed: 200,
                history: false,
              });
            }
          }

          this._lastSuccessfulEngravingText = engravingText;
          this._lastSuccessfulImageUrl = resultImageUrl;
          this._lastSuccessfulShopifyFilename = shopifyFilename;
          this._setPreviewImageLineItemProperty(resultImageUrl, shopifyFilename);
          this._publishAnalytics(LisaAiImageModal.analyticsEvents.previewGenerated, { wasCached, imageUrl: resultImageUrl, shopifyFilename, engravingText, generationMs: this._generationStartTime ? Date.now() - this._generationStartTime : null });
          this._applySuccessUI();
          this._observePreviewSeen(engravingText, resultImageUrl, shopifyFilename, latestRequestId);
          if (!wasCached) {
            this._incrementGenerationCount();
            this._enforceGenerationLimit();
          }
        } catch (error) {
          if (error?.name === 'AbortError' || latestRequestId !== this._generationRequestId) return;
          this._applyErrorUI(error);
        } finally {
          if (latestRequestId === this._generationRequestId) {
            this._generationInProgress = false;
            this._generationAbortController = null;
            this._setPreviewButtonLoading(false);
          }
        }
      }

      // ─── Network ──────────────────────────────────────────────────────────────

      async _fetchPreview(engravingText, signal) {
        const { isDev, referenceImages, productTitle, productId, overrideConfig } = this._settings;
        const baseUrl = isDev ? 'http://localhost:8787' : '/apps/lisa-ai-image';
        const referenceImageUrls = Array.isArray(referenceImages)
          ? referenceImages.filter(url => typeof url === 'string' && url.length)
          : [];

        const start = await fetch(`${baseUrl}/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referenceImageUrls,
            engravingText,
            productTitle,
            productId: String(productId),
            ...(overrideConfig ? { overrideConfig } : {}),
          }),
          signal,
        }).then(r => r.json());

        if (start.status === 'cached') return { imageUrl: start.imageUrl, shopifyFilename: start.shopifyFilename || null, wasCached: true };
        if (start.status !== 'pending' || !start.jobId) throw new Error('Failed to start job');

        const deadline = Date.now() + LisaAiImageModal.generationTimeoutMs;

        while (Date.now() < deadline) {
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

          await new Promise(r => setTimeout(r, LisaAiImageModal.statusPollIntervalMs));

          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

          const res = await fetch(`${baseUrl}/status/${encodeURIComponent(start.jobId)}`, { signal });
          const data = await res.json();

          if (!res.ok) throw new Error(data.error || 'Status check failed');
          if (data.status === 'pending') continue;
          if (data.status === 'complete') return { imageUrl: data.imageUrl, shopifyFilename: data.shopifyFilename || null, wasCached: false };

          throw new Error('Unexpected status response');
        }

        throw new Error('Generation timed out after 90 seconds');
      }

      async _createResultImage(resultImageUrl) {
        const img = document.createElement('img');
        img.src = resultImageUrl;
        img.className = LisaAiImageModal.classes.resultImage;

        if (img.complete && img.naturalWidth > 0) return img;

        try {
          await img.decode();
          return img;
        } catch (decodeError) {
          return new Promise((resolve, reject) => {
            img.addEventListener('load', () => resolve(img), { once: true });
            img.addEventListener('error', () => reject(decodeError), { once: true });
          });
        }
      }

      // ─── State & UI ───────────────────────────────────────────────────────────

      _setModalState(state) {
        this.dataset.state = state;
        this._setStateContainersVisibility(state);
      }

      _setStateContainersVisibility(state) {
        const { loadingContainer, errorMessage, resultContainer } = this.elements;
        const isGenerating = state === LisaAiImageModal.states.generating;
        const isError = state === LisaAiImageModal.states.error;
        const isSuccess = state === LisaAiImageModal.states.success;

        loadingContainer?.classList.toggle('hidden', !isGenerating);
        errorMessage?.classList.toggle('hidden', !isError);
        resultContainer?.classList.toggle('hidden', !isSuccess);
        resultContainer?.classList.toggle(LisaAiImageModal.classes.isVisible, isSuccess);
      }

      _applyGeneratingUI(engravingText) {
        const { closeButton, magnifyIcon, spinner } = this.elements;

        this._setModalState(LisaAiImageModal.states.generating);
        this._setPreviewButtonLoading(true);
        this._resetResultContainer();

        closeButton?.classList.add('hidden');
        magnifyIcon?.classList.add('hidden');
        spinner?.classList.remove('hidden');

        this._startLoaderAnimation(engravingText);
        this._startProgress();
      }

      _applySuccessUI() {
        const { closeButton, magnifyIcon, spinner } = this.elements;
        const hasLightbox = typeof SimpleLightbox !== 'undefined' && Boolean(this._lightbox);

        this._setModalState(LisaAiImageModal.states.success);
        this._stopLoaderAnimation();
        this._stopProgress();

        if (hasLightbox) magnifyIcon?.classList.remove('hidden');
        closeButton?.classList.remove('hidden');
        spinner?.classList.add('hidden');
      }

      _applyErrorUI(error) {
        console.error('AI Preview error:', error);
        this._setModalState(LisaAiImageModal.states.error);
        this._stopLoaderAnimation();
        this._stopProgress();
        this.elements.spinner?.classList.add('hidden');
        this._resetResultContainer();
        this._publishAnalytics(LisaAiImageModal.analyticsEvents.previewGenerateError, {
          errorMessage: error?.message || 'Unknown error',
        });
      }

      // ─── Button Loading ───────────────────────────────────────────────────────

      _setPreviewButtonLoading(isLoading) {
        if (!this.elements.previewButton) return;

        this._isPreviewButtonLoading = isLoading;
        this._togglePreviewButtonState?.();
      }

      _syncPreviewButtonLoadingState(inputValue = '') {
        const previewButton = this.elements.previewButton;
        if (!previewButton) return;

        const normalizedInput = inputValue.trim();
        const matchesActiveRequest = normalizedInput && normalizedInput === this._lastEngravingText;
        const matchesGeneratedPreview = normalizedInput && normalizedInput === this._lastSuccessfulEngravingText;
        const shouldShowGenerating = this._isPreviewButtonLoading && (matchesActiveRequest || matchesGeneratedPreview);
        const shouldShowReady = !shouldShowGenerating && matchesGeneratedPreview;

        previewButton.classList.toggle('is-generating', shouldShowGenerating);
        previewButton.classList.toggle('is-ready', shouldShowReady);
        previewButton.setAttribute('aria-busy', shouldShowGenerating ? 'true' : 'false');

        const label = previewButton.querySelector(LisaAiImageModal.selectors.previewButtonLabel);
        if (label) {
          if (shouldShowGenerating) {
            label.textContent = this._settings.generatingButtonText || 'Generating preview...';
          } else if (shouldShowReady) {
            label.textContent = this._settings.readyButtonText || previewButton.dataset.defaultText || this._settings.buttonText || '';
          } else {
            label.textContent = previewButton.dataset.defaultText || this._settings.buttonText || '';
          }
        }
      }

      // ─── Loader Animation ─────────────────────────────────────────────────────

      _startLoaderAnimation(engravingText = '') {
        const loaderContainer = this.elements.loaderContainer;
        if (!loaderContainer) return;

        // Prevent overlapping timers when a generation is restarted quickly.
        this._stopLoaderAnimation();

        const loaderTexts = (this._settings.loaderTexts || '')
          .split('\n')
          .map(line => line.trim().replaceAll('{CUSTOM_TEXT}', engravingText))
          .filter(Boolean);

        if (loaderTexts.length === 0) return;

        let currentIndex = 0;

        const rotateText = () => {
          const textEl = document.createElement('div');
          textEl.className = LisaAiImageModal.classes.loaderText;
          textEl.textContent = loaderTexts[currentIndex];
          loaderContainer.innerHTML = '';
          loaderContainer.appendChild(textEl);
          currentIndex = (currentIndex + 1) % loaderTexts.length;
        };

        rotateText();
        this._loaderInterval = setInterval(rotateText, LisaAiImageModal.loaderTextRotationMs);
      }

      _stopLoaderAnimation() {
        if (this._loaderInterval) {
          clearInterval(this._loaderInterval);
          this._loaderInterval = null;
        }

        const container = this.elements.loaderContainer;
        if (container) container.innerHTML = '';
      }

      _startProgress() {
        const { progressFill, progressPercent, progressContainer } = this.elements;
        if (!progressFill || !progressPercent || !progressContainer) return;

        this._stopProgress();
        this._progressStartTime = Date.now();

        const updateProgress = () => {
          const elapsed = Date.now() - this._progressStartTime;
          const ratio = Math.min(elapsed / LisaAiImageModal.progressDurationMs, 1);
          const easedRatio = 1 - ((1 - ratio) ** 3);
          const percent = Math.min(Math.floor(easedRatio * 100), LisaAiImageModal.progressMaxPercent);

          progressFill.style.width = `${percent}%`;
          progressPercent.textContent = `${percent}%`;
          progressContainer.setAttribute('aria-valuenow', String(percent));
        };

        updateProgress();
        this._progressInterval = setInterval(updateProgress, 200);
      }

      _stopProgress() {
        if (this._progressInterval) {
          clearInterval(this._progressInterval);
          this._progressInterval = null;
        }

        this._progressStartTime = null;

        const { progressFill, progressPercent, progressContainer } = this.elements;
        if (!progressFill || !progressPercent || !progressContainer) return;

        progressFill.style.width = '0%';
        progressPercent.textContent = '0%';
        progressContainer.setAttribute('aria-valuenow', '0');
      }

      // ─── DOM Helpers ──────────────────────────────────────────────────────────

      _getResultImageElement() {
        return this.elements.resultContainer?.querySelector(`.${LisaAiImageModal.classes.resultImage}`) || null;
      }

      _removeResultImageElement() {
        this._getResultImageElement()?.remove();
      }

      _resetResultContainer({ lightboxMediaUrl = null } = {}) {
        const resultMediaWrap = this.elements.resultMediaWrap;
        if (!resultMediaWrap) return;

        if (lightboxMediaUrl) {
          resultMediaWrap.setAttribute('data-lightbox-media', lightboxMediaUrl);
        } else {
          resultMediaWrap.removeAttribute('data-lightbox-media');
        }

        this._removeResultImageElement();
      }

      // ─── Lifecycle Helpers ────────────────────────────────────────────────────

      _showModal() {
        this.show(document.querySelector(LisaAiImageModal.selectors.previewButton));
      }

      _isModalOpen() {
        return this.hasAttribute('open');
      }

      _teardownOnClose() {
        this._abortCurrentGeneration();
        this._stopLoaderAnimation();
        this._stopProgress();
        this._pendingPreviewSeen = null;
        if (typeof SimpleLightbox !== 'undefined' && this._lightbox) {
          this._lightbox.destroy();
          this._lightbox = null;
        }
      }

      _abortCurrentGeneration() {
        if (!this._generationAbortController) return;

        this._generationAbortController.abort();
        this._generationAbortController = null;
        this._generationInProgress = false;
        this._generationRequestId++;
        this._setModalState(LisaAiImageModal.states.idle);
        this._setPreviewButtonLoading(false);
        this._stopProgress();
      }

      // ─── Analytics ────────────────────────────────────────────────────────────

      _publishAnalytics(eventName, data = {}) {
        const { productId, productTitle, productUrl } = this._settings;
        const market = window.themeConfig?.localization?.market?.handle || null;
        Shopify?.analytics?.publish(eventName, {
          productId: String(productId),
          productTitle,
          productUrl,
          market,
          ...data,
        });
      }

      _observePreviewSeen(engravingText = null, imageUrl = null, shopifyFilename = null, requestId = null) {
        this._pendingPreviewSeen = { engravingText, imageUrl, shopifyFilename, requestId };
        this._firePendingPreviewSeen();
      }

      _firePendingPreviewSeen() {
        if (!this._pendingPreviewSeen) return;
        if (!this._isModalOpen()) return;

        const { engravingText, imageUrl, shopifyFilename, requestId } = this._pendingPreviewSeen;
        this._pendingPreviewSeen = null;

        if (requestId !== null && requestId !== this._generationRequestId) return;
        this._publishAnalytics(LisaAiImageModal.analyticsEvents.previewPopupSeen, { engravingText, imageUrl, shopifyFilename });
      }

      // ─── Line Item Properties ──────────────────────────────────────────────────

      _setPreviewImageLineItemProperty(imageUrl, shopifyFilename = null) {
        const form = this.elements.productForm;
        if (!form) return;

        this._setHiddenFormInput(form, 'properties[_ai_preview_image_url]', imageUrl);
        this._setHiddenFormInput(form, 'properties[_ai_preview_shopify_filename]', shopifyFilename);
      }

      _setHiddenFormInput(form, name, value) {
        let input = form.querySelector(`input[name="${name}"]`);

        if (value) {
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
          }
          input.value = value;
        } else {
          input?.remove();
        }
      }

      // ─── Generation Limit ─────────────────────────────────────────────────────

      _getStorageKey() {
        return this._settings.max_per === 'product'
          ? `lisa_ai_gen_count_${this._settings.productId}`
          : 'lisa_ai_gen_count';
      }

      _getGenerationCount() {
        return parseInt(sessionStorage.getItem(this._getStorageKey()) || '0', 10);
      }

      _incrementGenerationCount() {
        sessionStorage.setItem(this._getStorageKey(), this._getGenerationCount() + 1);
      }

      _isAtGenerationLimit() {
        return Boolean(this._settings.max) && this._getGenerationCount() >= this._settings.max;
      }

      _enforceGenerationLimit() {
        if (this._isAtGenerationLimit()) {
          this.elements.previewButton?.classList.add('hidden');
        }
      }
    },
  );
}
